import React from 'react';
import { Box } from '@mui/material';
import {
  Smartphone,
  Checkroom,
  ShoppingBag,
  Diamond,
  Devices,
  Category,
  Laptop,
  Pets,
  Home,
  Chair,
  Bed,
  Kitchen,
  LocalLaundryService,
  Yard,
  Pool,
  Balcony,
  Garage,
  DirectionsCar,
  TwoWheeler,
  PedalBike,
  Train,
  Flight,
  DirectionsBoat,
  LocalGasStation,
  SportsSoccer,
  SportsBasketball,
  SportsTennis,
  SportsGolf,
  SportsVolleyball,
  SportsHockey,
  SportsBaseball,
  FitnessCenter,
  Hiking,
  Surfing,
  Build,
  Handyman,
  Construction,
  Plumbing,
  ElectricalServices,
  CleaningServices,
  Security,
  Lock,
  Key,
  LocalHospital,
  Medication,
  HealthAndSafety,
  Spa,
  LocalPharmacy,
  School,
  Work,
  Business,
  Science,
  Engineering,
  Restaurant,
  LocalPizza,
  LocalCafe,
  WineBar,
  Cake,
  Icecream,
  AccountBalanceWallet as WalletIcon,
  CreditCard,
  AttachMoney,
  Savings,
  TrendingUp,
  Description,
  Article,
  Folder,
  Archive,
  PictureAsPdf,
  Help,
  Info,
  Star,
  Favorite,
  Email,
  Phone,
  LocationOn,
  Event,
  Settings,
} from '@mui/icons-material';
import { SvgIconProps } from '@mui/material';
import { isFontAwesomeClassString } from '../utils/fontAwesomeClasses';
import { getApiOrigin } from '../config/environment';

// Mapping des noms d'icônes vers les icônes Material-UI
export const iconMapping: Record<string, React.ComponentType<SvgIconProps>> = {
  // Électronique
  'smartphone': Smartphone,
  'laptop': Laptop,
  'computer': Laptop,
  'tablet': Devices,
  'headphones': Devices,
  'speaker': Devices,
  'camera': Devices,
  'watch': Devices,
  'tv': Devices,
  'router': Devices,
  'memory': Devices,
  'storage': Devices,
  'devices': Devices,
  
  // Vêtements
  'clothes': Checkroom,
  'shopping-bag': ShoppingBag,
  'diamond': Diamond,
  'watch-clothes': Devices,
  
  // Maison
  'home': Home,
  'chair': Chair,
  'bed': Bed,
  'kitchen': Kitchen,
  'laundry': LocalLaundryService,
  'yard': Yard,
  'pool': Pool,
  'balcony': Balcony,
  'garage': Garage,
  
  // Transport
  'car': DirectionsCar,
  'motorcycle': TwoWheeler,
  'bike': PedalBike,
  'train': Train,
  'flight': Flight,
  'boat': DirectionsBoat,
  'gas-station': LocalGasStation,
  
  // Sports
  'soccer': SportsSoccer,
  'basketball': SportsBasketball,
  'tennis': SportsTennis,
  'golf': SportsGolf,
  'volleyball': SportsVolleyball,
  'hockey': SportsHockey,
  'baseball': SportsBaseball,
  'fitness': FitnessCenter,
  'hiking': Hiking,
  'surfing': Surfing,
  
  // Animaux
  'pets': Pets,
  
  // Outils
  'build': Build,
  'handyman': Handyman,
  'construction': Construction,
  'plumbing': Plumbing,
  'electrical': ElectricalServices,
  'cleaning': CleaningServices,
  'security': Security,
  'lock': Lock,
  'key': Key,
  
  // Santé
  'hospital': LocalHospital,
  'medication': Medication,
  'health': HealthAndSafety,
  'spa': Spa,
  'pharmacy': LocalPharmacy,
  
  // Éducation
  'school': School,
  'work': Work,
  'business': Business,
  'book': Business,
  'science': Science,
  'engineering': Engineering,
  
  // Nourriture
  'restaurant': Restaurant,
  'pizza': LocalPizza,
  'cafe': LocalCafe,
  'wine': WineBar,
  'cake': Cake,
  'icecream': Icecream,
  
  // Argent
  'wallet': WalletIcon,
  'credit-card': CreditCard,
  'money': AttachMoney,
  'savings': Savings,
  'trending-up': TrendingUp,
  
  // Documents
  'document': Description,
  'article': Article,
  'folder': Folder,
  'archive': Archive,
  'pdf': PictureAsPdf,
  
  // Divers
  'category': Category,
  'help': Help,
  'info': Info,
  'star': Star,
  'heart': Favorite,
  'email': Email,
  'phone': Phone,
  'location': LocationOn,
  'event': Event,
  'settings': Settings,
};

export const getCategoryMaterialIcon = (icon?: string): React.ComponentType<SvgIconProps> => {
  const normalized = icon?.toLowerCase().replace(/[\s-_]/g, '') || 'category';
  return iconMapping[normalized] || Category;
};

interface CategoryIconProps {
  icon?: string;
  /** Fichier SVG (public/uploads/pictos/…) — prioritaire sur l’heuristique `icon` */
  customIconFilename?: string | null;
  size?: 'small' | 'medium' | 'large';
  color?: string;
  style?: React.CSSProperties;
  className?: string;
}

const CategoryIcon: React.FC<CategoryIconProps> = ({
  icon = 'category',
  customIconFilename,
  size = 'medium',
  color,
  style,
  className,
}) => {
  const svgFilename =
    (customIconFilename && String(customIconFilename).trim()) ||
    (icon && icon.toLowerCase().endsWith('.svg') ? icon.trim() : '') ||
    '';

  if (svgFilename) {
    const getSizeValue = () => {
      switch (size) {
        case 'small':
          return 20;
        case 'large':
          return 48;
        default:
          return 32;
      }
    };
    const sz = getSizeValue();
    const iconUrl = `${getApiOrigin()}/uploads/pictos/${encodeURIComponent(svgFilename)}`;

    return (
      <Box
        className={className}
        style={{
          width: sz,
          height: sz,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: color || 'inherit',
          ...style,
        }}
      >
        <img
          src={iconUrl}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      </Box>
    );
  }

  // Font Awesome Free (classes complètes, ex. « fa-solid fa-film »)
  if (icon && isFontAwesomeClassString(icon)) {
    const getSizeValue = () => {
      switch (size) {
        case 'small':
          return 18;
        case 'large':
          return 40;
        default:
          return 28;
      }
    };
    return (
      <Box
        component="span"
        className={className}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: color || 'inherit',
          lineHeight: 1,
          ...style,
        }}
      >
        <i
          className={icon.trim()}
          aria-hidden
          style={{
            fontSize: getSizeValue(),
            lineHeight: 1,
          }}
        />
      </Box>
    );
  }

  // Normaliser le nom de l'icône (minuscules, sans espaces)
  const normalizedIcon = icon?.toLowerCase().replace(/[\s-_]/g, '') || 'category';
  
  // Récupérer l'icône Material-UI
  const MaterialIconComponent = getCategoryMaterialIcon(normalizedIcon);
  
  // Déterminer la taille
  const getSizeValue = () => {
    switch (size) {
      case 'small': return 20;
      case 'large': return 48;
      default: return 32;
    }
  };
  
  // Déterminer la couleur (hérite par défaut)
  const finalColor = color || 'inherit';
  
  return (
    <MaterialIconComponent
      className={className}
      style={{
        fontSize: getSizeValue(),
        color: finalColor,
        ...style,
      }}
    />
  );
};


export default CategoryIcon;
